import { useMemo } from 'react';
import { dropTargetToEndpoint } from '../types';
import { useCanvasStores } from '../stores/CanvasContext';
import { useDragEffect, type DragEffectConfig } from './useDragEffect';
import type { RedockDragRefValue } from '../stores/CanvasContext';

/**
 * Effect-only hook: registers global pointermove/pointerup/keydown listeners
 * for edge redock drag. Reads redockDragRef from context (set by useCanvasConnectorOps).
 */
export function useEdgeRedockEffect(): void {
  const { dragStore, store, redockDragRef } = useCanvasStores();

  const config = useMemo<DragEffectConfig<RedockDragRefValue>>(() => ({
    dragRef: redockDragRef,
    isActive: () => true,
    getExcludeEntityId: (drag) => drag.state.anchorEndpoint.entityId,
    onMove: (canvasPoint, drag, target) => {
      const updated = { ...drag.state, currentPoint: canvasPoint };
      redockDragRef.current = { ...drag, state: updated };
      dragStore.send({ type: 'redockDrag.set', drag: updated, dropTarget: target });
    },
    onDrop: (_canvasPoint, drag, target) => {
      if (target) {
        store.send({
          type: 'connector.batchRedock',
          connectorIds: drag.selectedEdgeIds,
          end: drag.end,
          newEndpoint: dropTargetToEndpoint(target),
        });
      }
    },
    onCleanup: () => {
      redockDragRef.current = null;
      dragStore.send({ type: 'redockDrag.clear' });
    },
  }), [redockDragRef, dragStore, store]);

  useDragEffect(config);
}
